"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  CONSENT_OPEN_EVENT,
  loadConsent,
  saveConsent,
  type ConsentRecord,
} from "../lib/consent";

type Phase = "hidden" | "banner" | "settings";

export default function CookieConsent() {
  const [phase, setPhase] = useState<Phase>("hidden");
  const [analytics, setAnalytics] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);

  // Show banner on first visit; re-open settings via dux:consent:open event
  // (dispatched by the "Cookie preferences" footer button and openConsentSettings()).
  useEffect(() => {
    // Init from localStorage after hydration — must be in useEffect to avoid
    // SSR/client hydration mismatch (server has no localStorage).
    const stored = loadConsent();
    if (!stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase("banner");
    } else {
      setAnalytics(stored.analytics);
    }
    const onOpen = () => {
      const current = loadConsent();
      setAnalytics(current?.analytics ?? false);
      setPhase("settings");
    };
    // The footer dispatches on `document`; consent.ts uses `window` — listen to both.
    document.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => {
      document.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
      window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
    };
  }, []);

  // Focus trap + Esc close for settings modal.
  useEffect(() => {
    if (phase !== "settings") return;
    lastFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const node = dialogRef.current;
    const focusables = node?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    focusables?.[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPhase("hidden");
        return;
      }
      if (e.key !== "Tab" || !node) return;
      const list = node.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lastFocusRef.current?.focus?.();
    };
  }, [phase]);

  const persist = useCallback((value: boolean): ConsentRecord => {
    setAnalytics(value);
    return saveConsent(value);
  }, []);

  const acceptAll = () => { persist(true);  setPhase("hidden"); };
  const rejectAll = () => { persist(false); setPhase("hidden"); };
  const savePreferences = () => { persist(analytics); setPhase("hidden"); };

  if (phase === "hidden") return null;

  return (
    <>
      {phase === "banner" && (
        <div role="region" aria-label="Cookie consent" className="consent-banner">
          <div className="consent-banner-inner">
            <div className="consent-banner-text">
              <strong className="consent-banner-title">We use cookies.</strong>{" "}
              Necessary cookies keep this site working. With your consent we also use analytics
              cookies to understand how visitors use the site so we can improve it.{" "}
              <Link href="/privacy" className="consent-banner-link">
                Read the privacy policy
              </Link>
              .
            </div>
            <div className="consent-banner-actions">
              <button
                type="button"
                className="consent-btn consent-btn-ghost"
                onClick={() => setPhase("settings")}
              >
                Customize
              </button>
              <button type="button" className="consent-btn consent-btn-secondary" onClick={rejectAll}>
                Reject all
              </button>
              <button type="button" className="consent-btn consent-btn-primary" onClick={acceptAll}>
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === "settings" && (
        <div
          className="consent-modal-backdrop"
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) setPhase("hidden"); }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-modal-title"
            className="consent-modal"
          >
            <div className="consent-modal-header">
              <h2 id="consent-modal-title" className="consent-modal-title">
                Cookie preferences
              </h2>
              <button
                type="button"
                aria-label="Close"
                className="consent-modal-close"
                onClick={() => setPhase("hidden")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="consent-modal-intro">
              Choose which categories of cookies and similar storage technologies you allow
              on this site. You can change your choice any time from the footer.
            </p>

            <div className="consent-category">
              <div className="consent-category-row">
                <div>
                  <span className="consent-category-name">Strictly necessary</span>
                  <span className="consent-category-pill">Always on</span>
                </div>
                <span aria-hidden="true" className="consent-toggle is-locked">
                  <span className="consent-toggle-knob" />
                </span>
              </div>
              <p className="consent-category-desc">
                Required for the site to work — for example, remembering your active chat
                session and storing your consent choice itself. Cannot be turned off.
              </p>
            </div>

            <div className="consent-category">
              <div className="consent-category-row">
                <div>
                  <span className="consent-category-name">Analytics</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analytics}
                  aria-label="Toggle analytics cookies"
                  className={`consent-toggle${analytics ? " is-on" : ""}`}
                  onClick={() => setAnalytics((v) => !v)}
                >
                  <span className="consent-toggle-knob" />
                </button>
              </div>
              <p className="consent-category-desc">
                Anonymous traffic and performance data so we can understand how visitors use
                the site and improve it. Disabled until you opt in.
              </p>
            </div>

            <div className="consent-modal-actions">
              <button type="button" className="consent-btn consent-btn-secondary" onClick={rejectAll}>
                Reject all
              </button>
              <button type="button" className="consent-btn consent-btn-primary" onClick={savePreferences}>
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
